/**
 * BatteryHistoryChart - Gráfico y estadísticas del historial de batería
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO, subDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  Battery,
  TrendingDown,
  TrendingUp,
  Minus,
  AlertTriangle,
} from 'lucide-react';
import { controllersService } from '@/services';

interface BatteryHistoryChartProps {
  controllerId: string;
  controllerName?: string;
}

export function BatteryHistoryChart({ controllerId, controllerName }: BatteryHistoryChartProps) {
  const [since, setSince] = useState(() => {
    return subDays(new Date(), 7).toISOString().split('T')[0];
  });
  const [until, setUntil] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  const { data: trendData, isLoading: isLoadingTrend } = useQuery({
    queryKey: ['controller-battery-trend', controllerId, since, until],
    queryFn: async () => {
      const response = await controllersService.getBatteryTrend(controllerId, since, until);
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar tendencia');
      }
      return response.data;
    },
  });

  const { data: statsData, isLoading: isLoadingStats } = useQuery({
    queryKey: ['controller-battery-stats', controllerId],
    queryFn: async () => {
      const response = await controllersService.getBatteryStats(controllerId);
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar estadísticas');
      }
      return response.data;
    },
  });

  const trend = trendData?.data || [];
  const stats = statsData?.data;

  // Preparar datos para el gráfico
  const chartData = trend.map((item) => ({
    fecha: format(parseISO(item.timestamp), 'dd/MM', { locale: es }),
    timestamp: item.timestamp,
    bateria: item.batteryLevel,
  }));

  const getBatteryColor = (level: number) => {
    if (level > 50) return 'text-green-600';
    if (level > 20) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getBatteryBadgeColor = (level: number) => {
    if (level > 50) return 'bg-green-500/10 text-green-600 border-green-500/20';
    if (level > 20) return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20';
    return 'bg-red-500/10 text-red-600 border-red-500/20';
  };

  return (
    <div className="space-y-4">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-muted-foreground">Nivel actual</div>
              <div className={cn('text-2xl font-bold flex items-center gap-2', getBatteryColor(stats.currentLevel))}>
                <Battery className="w-5 h-5" />
                {stats.currentLevel}%
              </div>
              {stats.levelChange24h !== undefined && (
                <div className="flex items-center gap-1 mt-1 text-xs">
                  {stats.levelChange24h > 0 ? (
                    <TrendingUp className="w-3 h-3 text-green-600" />
                  ) : stats.levelChange24h < 0 ? (
                    <TrendingDown className="w-3 h-3 text-red-600" />
                  ) : (
                    <Minus className="w-3 h-3 text-muted-foreground" />
                  )}
                  <span className={cn(
                    stats.levelChange24h > 0 ? 'text-green-600' : stats.levelChange24h < 0 ? 'text-red-600' : 'text-muted-foreground'
                  )}>
                    {stats.levelChange24h > 0 ? '+' : ''}{stats.levelChange24h}% en 24h
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-muted-foreground">Mínimo</div>
              <div className="text-2xl font-bold text-red-600">{stats.minLevel}%</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-muted-foreground">Máximo</div>
              <div className="text-2xl font-bold text-green-600">{stats.maxLevel}%</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-muted-foreground">Promedio</div>
              <div className="text-2xl font-bold">{stats.avgLevel.toFixed(1)}%</div>
              <div className="text-xs text-muted-foreground mt-1">
                {stats.readingsCount} lecturas
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Alerts */}
      {stats && stats.currentLevel < 20 && (
        <Card className="border-yellow-500/50 bg-yellow-500/5">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-yellow-600">
              <AlertTriangle className="w-4 h-4" />
              <span className="font-medium">Batería baja: {stats.currentLevel}%</span>
            </div>
          </CardContent>
        </Card>
      )}

      {stats && stats.levelChange24h !== undefined && stats.levelChange24h < -5 && (
        <Card className="border-orange-500/50 bg-orange-500/5">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="w-4 h-4" />
              <span className="font-medium">
                Batería descendiendo rápido: {stats.levelChange24h}% en 24h
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Date Range Filter */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Rango de fechas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Desde</Label>
              <Input
                type="date"
                value={since}
                onChange={(e) => setSince(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Hasta</Label>
              <Input
                type="date"
                value={until}
                onChange={(e) => setUntil(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tendencia de batería</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoadingTrend ? (
            <Skeleton className="h-[300px] w-full" />
          ) : chartData.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No hay datos de batería en el período seleccionado
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="fecha"
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fontSize: 12 }}
                  label={{ value: 'Batería (%)', angle: -90, position: 'insideLeft' }}
                />
                <RechartsTooltip
                  formatter={(value: number) => [`${value}%`, 'Batería']}
                  labelFormatter={(label) => `Fecha: ${label}`}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="bateria"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name="Batería (%)"
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
